import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { BRAND } from "@/lib/brand";

const SECTIONS = [
    { id: "preguntas", label: "Tres preguntas" },
    { id: "metodo", label: "El método" },
    { id: "sistema", label: "El sistema" },
    { id: "equipo", label: "Quiénes somos" },
];
const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

export default function Navigation() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const isHome = pathname === "/";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 32);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    useEffect(() => {
        const onEsc = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", onEsc);
        return () => window.removeEventListener("keydown", onEsc);
    }, []);

    const goToSection = (id) => {
        setOpen(false);
        if (!isHome) {
            navigate(`/#${id}`);
            return;
        }
        setTimeout(() => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 380);
    };

    const surface = (scrolled || !isHome) && !open;

    return (
        <>
            <header
                data-testid="site-nav"
                className={`fixed top-0 left-0 right-0 z-[60] h-16 md:h-20 transition-colors duration-500 ${
                    open
                        ? "bg-transparent"
                        : surface
                          ? "bg-white/95 backdrop-blur border-b border-[#E5E0D5]"
                          : "bg-transparent"
                }`}
            >
                <div className="relative max-w-[1200px] mx-auto h-full px-6 md:px-12">
                    <button
                        data-testid="nav-menu-toggle"
                        aria-label={open ? "Cerrar menú" : "Abrir menú"}
                        aria-expanded={open}
                        onClick={() => setOpen((v) => !v)}
                        style={{
                            transitionProperty: "left, transform, opacity",
                            transitionDuration: "700ms",
                            transitionTimingFunction: EASE,
                            top: "50%",
                            left: open ? "0px" : "50%",
                            transform: open ? "translate(0,-50%)" : "translate(-50%,-50%)",
                        }}
                        className="absolute inline-flex items-center justify-center rounded-full hover:opacity-90 active:scale-95"
                    >
                        <img
                            src={BRAND.logoMonogramGoldOnWhite}
                            alt="Aurum Vanguard"
                            className="h-10 w-10 md:h-12 md:w-12 object-contain select-none"
                            draggable={false}
                        />
                    </button>

                    <button
                        aria-label="Cerrar menú"
                        data-testid="nav-menu-close"
                        onClick={() => setOpen(false)}
                        style={{
                            transition: `opacity 400ms ${EASE}`,
                            opacity: open ? 1 : 0,
                            pointerEvents: open ? "auto" : "none",
                        }}
                        className="absolute right-6 md:right-12 top-1/2 -translate-y-1/2 text-[#F4F1EA] hover:text-[#8B6914]"
                    >
                        <X size={26} />
                    </button>
                </div>
            </header>

            <div
                data-testid="nav-overlay"
                aria-hidden={!open}
                style={{
                    transition: `opacity 600ms ${EASE}`,
                    opacity: open ? 1 : 0,
                    pointerEvents: open ? "auto" : "none",
                }}
                className="fixed inset-0 z-[55]"
            >
                <div className="absolute inset-0 bg-[#1A1A1A]" />
                <div className="relative h-full w-full text-[#F4F1EA] overflow-y-auto pt-16 md:pt-20">
                    <div
                        style={{
                            transition: `transform 700ms ${EASE}, opacity 700ms ${EASE}`,
                            transitionDelay: open ? "120ms" : "0ms",
                            transform: open ? "translateY(0)" : "translateY(12px)",
                            opacity: open ? 1 : 0,
                        }}
                        className="max-w-[1200px] mx-auto px-6 md:px-12 pt-10 md:pt-16 pb-20"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
                            <nav
                                aria-label="Menú principal"
                                className="md:col-span-8 flex flex-col gap-6 md:gap-7"
                            >
                                {SECTIONS.map((s, i) => (
                                    <button
                                        key={s.id}
                                        data-testid={`overlay-link-${s.id}`}
                                        onClick={() => goToSection(s.id)}
                                        style={{
                                            transition: `transform 700ms ${EASE}, opacity 700ms ${EASE}`,
                                            transitionDelay: open ? `${180 + i * 80}ms` : "0ms",
                                            transform: open ? "translateY(0)" : "translateY(20px)",
                                            opacity: open ? 1 : 0,
                                        }}
                                        className="font-serif-display text-left text-4xl md:text-6xl tracking-tight text-[#F4F1EA] hover:text-[#8B6914] transition-colors duration-200"
                                    >
                                        {s.label}
                                    </button>
                                ))}
                                <Link
                                    to="/contacto"
                                    onClick={() => setOpen(false)}
                                    data-testid="overlay-cta"
                                    style={{
                                        transition: `transform 700ms ${EASE}, opacity 700ms ${EASE}`,
                                        transitionDelay: open ? `${180 + SECTIONS.length * 80}ms` : "0ms",
                                        transform: open ? "translateY(0)" : "translateY(20px)",
                                        opacity: open ? 1 : 0,
                                    }}
                                    className="font-serif-display italic text-left text-4xl md:text-6xl tracking-tight text-[#8B6914] hover:text-[#F4F1EA] transition-colors duration-200"
                                >
                                    Hablemos.
                                </Link>
                            </nav>

                            <aside
                                style={{
                                    transition: `transform 700ms ${EASE}, opacity 700ms ${EASE}`,
                                    transitionDelay: open ? "320ms" : "0ms",
                                    transform: open ? "translateY(0)" : "translateY(20px)",
                                    opacity: open ? 1 : 0,
                                }}
                                className="md:col-span-4 md:border-l md:border-[#2A2A2A] md:pl-12"
                            >
                                <div>
                                    <p className="text-xs tracking-[0.24em] uppercase text-[#8B6914] mb-4">
                                        Correo directo
                                    </p>
                                    <a
                                        href={`mailto:${BRAND.contact.email}`}
                                        className="text-sm text-[#F4F1EA] hover:text-[#8B6914] transition-colors break-all"
                                    >
                                        {BRAND.contact.email}
                                    </a>
                                </div>
                                <div className="mt-10 pt-8 border-t border-[#2A2A2A]">
                                    <p className="text-xs tracking-[0.24em] uppercase text-[#8B6914] mb-3">
                                        Dónde estamos
                                    </p>
                                    <p className="text-sm text-[#A4A19A]">
                                        Strategic Growth & Innovation Firm
                                        <br />
                                        Dubai, Emiratos Árabes Unidos
                                    </p>
                                </div>
                            </aside>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
